import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import {
  getClientSurveyDetail,
  resendClientSurvey,
  createSurveyFollowup,
} from "../../api/clientSurveys";
import "../MarketingSectionCompact.css";

const ratingRows = [
  { key: "overall_rating", label: "Overall" },
  { key: "salon_rating", label: "Salon" },
  { key: "receptionist_rating", label: "Reception" },
  { key: "service_rating", label: "Service" },
  { key: "staff_rating", label: "Staff" },
  { key: "online_booking_rating", label: "Online booking" },
];

const promptLabel = (status) => {
  if (!status) return "Not asked";
  if (status === "clicked") return "Clicked";
  if (status === "prompted") return "Prompted";
  if (status === "scheduled") return "Scheduled";
  return "Pending";
};

export default function ClientSurveyDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { token, currentSalon } = useAuth();
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);
  const [creatingFollowup, setCreatingFollowup] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getClientSurveyDetail(requestId, token);
      setDetail(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load survey detail.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, token]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleResend = async () => {
    if (!requestId) return;
    setResending(true);
    setMessage("");
    try {
      const data = await resendClientSurvey(requestId, token);
      if (data?.request_id && String(data.request_id) !== String(requestId)) {
        navigate(`/manager/marketing/client-surveys/${data.request_id}`);
        return;
      }
      setMessage("Survey resend queued.");
      await loadDetail();
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Unable to resend survey.");
    } finally {
      setResending(false);
    }
  };

  const handleCreateFollowup = async () => {
    if (!requestId) return;
    setCreatingFollowup(true);
    setMessage("");
    try {
      await createSurveyFollowup(requestId, token);
      setMessage("Follow-up created.");
      await loadDetail();
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Unable to create follow-up.");
    } finally {
      setCreatingFollowup(false);
    }
  };

  const canResend = detail?.status === "pending" || detail?.status === "expired";
  const canCreateFollowup =
    detail?.response_id && !detail?.followup_id && detail?.overall_rating != null;
  const canOpenTicket = Boolean(detail?.ticket_id);

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-medium marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Survey Detail
            </h1>
            <button
              type="button"
              onClick={() => navigate("/manager/marketing/client-surveys")}
              className="rounded-full border border-white/60 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/10"
            >
              Back to Client Surveys
            </button>
          </div>
          <div className="px-6 py-6 space-y-6">
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-600">
                Loading survey detail...
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}
            {message && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-600">
                {message}
              </div>
            )}

            {!loading && detail && (
              <>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                  <div className="text-lg font-semibold text-slate-900">
                    Ticket {detail.ticket_number || detail.ticket_id || "-"}
                  </div>
                  <div className="text-sm text-slate-500">
                    Client: {detail.client_name || "-"}
                  </div>
                  <div className="text-sm text-slate-500">
                    Staff: {detail.staff_name || "-"}
                  </div>
                  <div className="text-sm text-slate-500">
                    Status: {detail.status}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {canOpenTicket && (
                    <button
                      type="button"
                      onClick={() => navigate(`/tickets/${detail.ticket_id}`)}
                      className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      Open ticket
                    </button>
                  )}
                  {canResend && (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {resending ? "Resending..." : "Resend survey"}
                    </button>
                  )}
                  {canCreateFollowup && (
                    <button
                      type="button"
                      onClick={handleCreateFollowup}
                      disabled={creatingFollowup}
                      className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {creatingFollowup ? "Creating..." : "Create follow-up"}
                    </button>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Ratings
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {ratingRows
                      .filter((row) =>
                        row.key !== "online_booking_rating" || detail.show_online_booking_rating
                      )
                      .map((row) => (
                        <div
                          key={row.key}
                          className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                        >
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            {row.label}
                          </div>
                          <div className="text-lg font-semibold text-slate-900">
                            {detail[row.key] || "-"}
                          </div>
                        </div>
                      ))}
                  </div>
                  <div className="text-sm text-slate-500">
                    Submitted:
                    {detail.response_created_at_utc
                      ? ` ${formatDateTimeInTimeZone(detail.response_created_at_utc, salonTimezone)}`
                      : " -"}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Comment
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">
                    {detail.comment || "No comment provided."}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Follow-up
                  </div>
                  {detail.followup_id ? (
                    <div className="text-sm text-slate-700">
                      Status: {detail.followup_status} | Priority: {detail.followup_priority}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No follow-up created.</div>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Public review prompt
                  </div>
                  <div className="text-sm text-slate-700">
                    Status: {promptLabel(detail.public_review_prompt_status)}
                  </div>
                  {detail.public_review_prompt_mode && (
                    <div className="text-sm text-slate-500">
                      Mode: {detail.public_review_prompt_mode}
                    </div>
                  )}
                  {detail.public_review_prompt_clicked_at_utc && (
                    <div className="text-sm text-slate-500">
                      Clicked:
                      {` ${formatDateTimeInTimeZone(
                        detail.public_review_prompt_clicked_at_utc,
                        salonTimezone
                      )}`}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Timeline
                  </div>
                  <div className="text-sm text-slate-500">
                    Sent:
                    {detail.sent_at_utc
                      ? ` ${formatDateTimeInTimeZone(detail.sent_at_utc, salonTimezone)}`
                      : " -"}
                  </div>
                  <div className="text-sm text-slate-500">
                    Expires:
                    {detail.expires_at_utc
                      ? ` ${formatDateTimeInTimeZone(detail.expires_at_utc, salonTimezone)}`
                      : " -"}
                  </div>
                  <div className="text-sm text-slate-500">
                    Created:
                    {detail.created_at_utc
                      ? ` ${formatDateTimeInTimeZone(detail.created_at_utc, salonTimezone)}`
                      : " -"}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
